import React, { useState } from 'react';
import PostActionRow from './PostActionRow';
import EventDetailsDrawer from './EventDetailsDrawer';

/**
 * Event List Item — rendered as a feed "post".
 * Author = hosting business (clickable avatar + name, IG handle only when the
 * business has an avatar image). Actions: Tickets/RSVP/Info (external) or
 * Details (opens a details drawer) + Share. No commenting/posting.
 * Poster (if available) is shown as the post's image.
 */
export default function EventListItem({ event, business, location, analytics, shareBaseUrl, showAuthor = true }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [showAllDates, setShowAllDates] = useState(false);

    const formatTime = (time) => {
        if (!time) return '';
        const [hours, minutes] = time.split(':');
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayHour = h % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleClick = (label) => {
        if (analytics) {
            analytics.trackInteraction(label, {
                eventId: event.id,
                eventTitle: event.title,
                businessId: business?.id
            });
        }
    };

    const primaryUrl = event.ticket_url || event.rsvp_url || event.info_url;
    const urlLabel = event.ticket_url ? 'Tickets' :
        event.rsvp_url ? 'RSVP' :
            event.info_url ? 'More Info' : null;

    const businessUrl = business ? `/businesses/${business.id}/` : `/events/${event.id}/`;
    const avatar = business?.avatar
        ? `https://tacxuksfiabbdygcfnqr.supabase.co/storage/v1/object/public/media/businesses/${business.id}/${business.avatar}`
        : null;

    const igUrl = avatar && business?.handle
        ? `https://instagram.com/${business.handle.replace(/^@/, '')}`
        : null;

    const eventCharge = event.price
        ? (event.price.amount === 0 || event.price.description === 'Free' ? 'Free' :
            event.price.amount > 0 ? `$${event.price.amount} ${event.price.description || ''}`.trim() :
            (event.price.description || ''))
        : '';

    const dateLine = event.start_time
        ? `${formatDate(event.eventDate)} · ${formatTime(event.start_time)}`
        : formatDate(event.eventDate);

    // Recurring series: every performance except this card's own date, so the
    // card can link out to each one. Only present on collapsed business pages.
    const SERIES_PREVIEW_COUNT = 6;
    const otherInstances = Array.isArray(event.seriesInstances)
        ? event.seriesInstances.filter((instance) => instance && instance.id && instance.id !== event.id)
        : [];
    const totalDates = otherInstances.length + 1;
    const visibleInstances = showAllDates
        ? otherInstances
        : otherInstances.slice(0, SERIES_PREVIEW_COUNT);

    const handleOpenDetails = () => {
        handleClick('click_event_details');
        setDrawerOpen(true);
    };

    return (
        <article>
            {/*
              Instagram-style header: avatar, business name and handle live
              OUTSIDE the bordered card, above it.
            */}
            {showAuthor && (
                <div className="post-head">
                    {avatar && (
                        <a href={businessUrl} onClick={() => handleClick('click_event_post_author')} className="flex-shrink-0 no-underline" aria-label={business?.title || 'View event'}>
                            <div className="story-ring">
                                <img src={avatar} alt={business?.title} className="post-avatar" loading="lazy" />
                            </div>
                        </a>
                    )}
                    <div className="post-author">
                        <a href={businessUrl} onClick={() => handleClick('click_event_post_author')} className="no-underline group">
                            <p className="post-name group-hover:text-brand-700 transition-colors">{business?.title || event.title}</p>
                        </a>
                        {igUrl && (
                            <a href={igUrl} target="_blank" rel="noopener noreferrer" className="no-underline hover:text-brand-700">
                                <p className="post-handle">{business.handle}</p>
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/*
              Bordered card. Corner radius is sharpened to rounded-lg and
              overflow-hidden so the edge-to-edge poster clips to those corners.
            */}
            <div className="bg-white border border-gray-200/70 rounded-md overflow-hidden" style={{ boxShadow: '0 1px 2px rgb(0 0 0 / 0.04)' }}>
                {/* Poster when available — edge-to-edge full width, natural height, no crop */}
                {event.poster && (
                    <a href={`/events/${event.id}/`} className="block no-underline">
                        <img
                            src={event.poster}
                            alt={event.title}
                            className="w-full h-auto object-contain"
                            loading="lazy"
                            onClick={() => handleClick('click_event_poster')}
                        />
                    </a>
                )}

                <div className="p-4 md:p-5">
                    <h3 className="post-title" style={{ fontSize: '1.15rem' }}>
                        <a href={`/events/${event.id}/`} className="hover:text-brand-700 no-underline" onClick={() => handleClick('click_event_list_item')}>
                            {event.title}
                        </a>
                    </h3>

                    {/* Date/time on its own line beneath the title */}
                    {dateLine && <p className="text-sm text-gray-500 mb-3">{dateLine}</p>}

                    {event.description && (
                        <p className="post-body text-sm line-clamp-3 mb-3">{event.description}</p>
                    )}

                    {/* Recurring series — links to every other performance */}
                    {otherInstances.length > 0 && (
                        <div className="mb-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Also showing</p>
                            <div className="flex flex-wrap gap-1.5">
                                {visibleInstances.map((instance) => (
                                    <a
                                        key={instance.id}
                                        href={`/events/${instance.id}/`}
                                        onClick={() => handleClick('click_event_series_date')}
                                        className="inline-block px-2.5 py-1 text-xs font-medium rounded-full bg-brand-50 text-brand-800 border border-brand-100 hover:bg-brand-100 no-underline transition-colors"
                                    >
                                        {formatDate(instance.date)}
                                    </a>
                                ))}
                            </div>
                            {otherInstances.length > SERIES_PREVIEW_COUNT && (
                                <button
                                    type="button"
                                    onClick={() => setShowAllDates((value) => !value)}
                                    className="mt-2 text-xs font-semibold text-brand-700 hover:text-brand-800"
                                >
                                    {showAllDates ? 'Show fewer dates' : `See all ${totalDates} dates`}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Meta row */}
                    {(location || event.over_21 || event.over_18 || event.virtual || event.canceled || event.sold_out) && (
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            {location && (
                                <span className="text-sm text-gray-500 inline-flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                                    </svg>
                                    {location.name}
                                </span>
                            )}
                            {event.over_21 && <span className="badge badge-pink text-xs">21+</span>}
                            {event.over_18 && <span className="badge badge-pink text-xs">18+</span>}
                            {event.virtual && <span className="badge badge-green text-xs">Virtual</span>}
                            {event.canceled && <span className="badge bg-red-100 text-red-800 text-xs">Canceled</span>}
                            {event.sold_out && <span className="badge bg-red-100 text-red-800 text-xs">Sold Out</span>}
                        </div>
                    )}

                    <PostActionRow
                        shareUrl={(shareBaseUrl || '') + `/events/${event.id}/`}
                        shareTitle={event.title}
                        openUrl={primaryUrl || `/events/${event.id}/`}
                        openLabel={urlLabel || 'Details'}
                        openExternal={Boolean(primaryUrl)}
                        onOpenDetails={primaryUrl ? null : handleOpenDetails}
                        bookmarkType="event"
                        bookmarkId={event.id}
                        bookmarkTitle={event.title}
                        bookmarkDate={event.eventDate}
                        bookmarkTime={event.start_time}
                        bookmarkCharge={eventCharge}
                        analytics={analytics}
                    />
                </div>
            </div>

            {drawerOpen && (
                <EventDetailsDrawer
                    business={business}
                    event={event}
                    location={location}
                    analytics={analytics}
                    onClose={() => setDrawerOpen(false)}
                    position="fixed"
                    trackNs="event"
                />
            )}
        </article>
    );
}